import { BodyLinkV3, ButtonV3, TypographyV3 } from '@jouzen/ecom-components';
import { usePathname } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import type { ReactNode } from 'react';

import { EventType } from '@/analytics/types';
import getLocalePartialUrl from '@/utils/getLocalePartialUrl';
import slugifyString from '@/utils/slugifyString';

import Feature from './Feature';

interface WhoGIftsForProps {
  readonly position: 'top' | 'bottom';
}

const ShopButton = (): JSX.Element => {
  const path = usePathname();
  const t = useTranslations();

  const handleClick = async () => {
    void window.ouraAnalytics.track(EventType.CTAClicked, {
      cta: slugifyString(t('header_shop_now')),
      location: 'perfect_gift_module',
      module: 'perfect_gift_module',
      path,
    });
  };

  return (
    <ButtonV3 onClick={handleClick} href="/product/rings">
      {t('header_shop_now')}
    </ButtonV3>
  );
};

const WhoGiftsFor = ({ position = 'top' }: WhoGIftsForProps): JSX.Element => {
  const t = useTranslations();
  const locale = useLocale();
  const localePartialUrl = getLocalePartialUrl(locale);

  return (
    <section className="py-20">
      {position === 'top' && (
        <>
          <Feature
            title="starting_out_title"
            body={t.rich('starting_out_body', {
              trendslink(chunks: ReactNode) {
                return (
                  <BodyLinkV3
                    href={`https://ouraring.com/blog${localePartialUrl}trends/`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {chunks}
                  </BodyLinkV3>
                );
              },
              goalslink(chunks: ReactNode) {
                return (
                  <BodyLinkV3
                    href={`https://ouraring.com/blog${localePartialUrl}new-personalized-activity-features/`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {chunks}
                  </BodyLinkV3>
                );
              },
            })}
            reverse
            image={{
              src: t('gifting-starting-out-activity-image'),
              localizedAlt: 'starting_out_title',
              width: 600,
              height: 693,
            }}
          />
          <Feature
            title="powerful_women_title"
            body={t.rich('powerful_women_body', {
              periodlink(chunks: ReactNode) {
                return (
                  <BodyLinkV3
                    href={`https://ouraring.com/blog${localePartialUrl}period-prediction/`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {chunks}
                  </BodyLinkV3>
                );
              },
            })}
            image={{
              src: 'gift/powerful-women',
              width: 600,
              height: 693,
              localizedAlt: 'powerful_women_title',
            }}
          />
          <Feature
            title="sleep_deprived_parent_title"
            body={t.rich('sleep_deprived_parent_body', {
              sleeplink(chunks: ReactNode) {
                return (
                  <BodyLinkV3
                    href={`https://ouraring.com/blog${localePartialUrl}sleep-new-parents/`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {chunks}
                  </BodyLinkV3>
                );
              },
            })}
            reverse
            image={{
              src: 'gift/sleepy-parent',
              width: 600,
              height: 693,
              localizedAlt: 'sleep_deprived_parent_title',
            }}
            overlayImage={{
              src: t('gifting-sleep-trends-mock-image'),
              fill: true,
              localizedAlt: 'sleep_deprived_parent_title',
              format: 'png',
            }}
          />
          <Feature
            title="fashionista_title"
            body={t.rich('fashionista_body', {
              pdplink(chunks: ReactNode) {
                return (
                  <BodyLinkV3 href="/product/horizon-silver">
                    {chunks}
                  </BodyLinkV3>
                );
              },
            })}
            image={{
              src: 'gift/fashionista',
              width: 600,
              height: 693,
              localizedAlt: 'fashionista_title',
            }}
          />
        </>
      )}
      {position === 'bottom' && (
        <>
          <Feature
            title="fitness_fanatics_title"
            body={t.rich('fitness_fanatics_body', {
              activitylink(chunks: ReactNode) {
                return (
                  <BodyLinkV3
                    href={`https://ouraring.com/blog${localePartialUrl}automatic-activity-detection/`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {chunks}
                  </BodyLinkV3>
                );
              },
              goalslink(chunks: ReactNode) {
                return (
                  <BodyLinkV3
                    href={`https://ouraring.com/blog${localePartialUrl}new-personalized-activity-features/`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {chunks}
                  </BodyLinkV3>
                );
              },
            })}
            image={{
              src: 'gift/fitness-fanatics',
              localizedAlt: 'fitness_fanatics_title',
              width: 600,
              height: 693,
            }}
            overlayImage={{
              src: t('gifting-fitness-fanatics-image'),
              localizedAlt: 'fitness_fanatics_title',
              fill: true,
              format: 'png',
            }}
            reverse
          />
          <Feature
            title="frequent_travelers_title"
            body={t.rich('frequent_travelers_body', {
              travellink(chunks: ReactNode) {
                return (
                  <BodyLinkV3
                    href={`https://ouraring.com/blog${localePartialUrl}manage-jet-lag/`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {chunks}
                  </BodyLinkV3>
                );
              },
            })}
            image={{
              src: t('gifting-frequent-travelers-image'),
              localizedAlt: 'frequent_travelers_title',
              width: 600,
              height: 693,
            }}
          />
          <Feature
            title="nightowls_earlybirds_title"
            body={t.rich('nightowls_earlybirds_body', {
              bedtimelink(chunks: ReactNode) {
                return (
                  <BodyLinkV3
                    href={`https://ouraring.com/blog${localePartialUrl}ideal-bedtime/`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {chunks}
                  </BodyLinkV3>
                );
              },
            })}
            image={{
              src: t('gifting-nightowls-earlybirds-image'),
              localizedAlt: 'nightowls_earlybirds_title',
              width: 600,
              height: 693,
            }}
            reverse
          />
          <Feature
            title="naptakers_title"
            body={t.rich('naptakers_body', {
              naplink(chunks: ReactNode) {
                return (
                  <BodyLinkV3
                    href="https://support.ouraring.com/hc/articles/1500009653181"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {chunks}
                  </BodyLinkV3>
                );
              },
              readinesslink(chunks: ReactNode) {
                return (
                  <BodyLinkV3
                    href={`https://ouraring.com/blog${localePartialUrl}readiness-score/`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {chunks}
                  </BodyLinkV3>
                );
              },
            })}
            image={{
              src: 'gift/nap-takers',
              width: 600,
              height: 693,
              localizedAlt: 'naptakers_title',
            }}
            overlayImage={{
              src: t('gifting-nap-taker-image'),
              fill: true,
              format: 'png',
              localizedAlt: 'naptakers_title',
            }}
          />
          <Feature
            title="anyone_everyone_title"
            body={<TypographyV3>{t('anyone_everyone_body')}</TypographyV3>}
            button={<ShopButton />}
            image={{
              src: 'gift/anyone-everyone',
              width: 600,
              height: 693,
              localizedAlt: 'anyone_everyone_title',
            }}
            reverse
          />
        </>
      )}
    </section>
  );
};

export default WhoGiftsFor;
